import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import DOMPurify from 'dompurify';
import Layout from './Layout/Layout';
import { getCdnImageUrl } from '../utils/cdn';
import './ContentDisplayOne.css';
import './HomePage.css';

const renderStarRatingHtml = (num, max = 5) => {
  const fullStars = Math.floor(num);
  const hasHalf = (num % 1) >= 0.25 && (num % 1) <= 0.75;
  const emptyStars = max - fullStars - (hasHalf ? 1 : 0);

  let starsStr = '★'.repeat(fullStars);
  if (hasHalf) starsStr += '½';
  starsStr += '☆'.repeat(Math.max(0, emptyStars));

  return `<span class="star-rating-badge" title="${num}/${max} Rating"><span class="star-gold">${starsStr}</span> <span class="rating-text">(${num}/${max})</span></span>`;
};

const processHtmlWithRatings = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') return '';

  let result = htmlContent;

  // Rating: X/5 or Rating: X.X/5
  result = result.replace(/Rating:\s*([0-5](?:\.[0-9])?)\s*\/\s*5/gi, (match, score) => {
    const val = parseFloat(score);
    return `<strong class="rating-label">Rating:</strong> ${renderStarRatingHtml(val)}`;
  });

  // Rating: X.X or Rating: X (standalone)
  result = result.replace(/Rating:\s*([0-5](?:\.[0-9])?)\s*(?=[<,\s\n]|$)(?!\/)/gi, (match, score) => {
    const val = parseFloat(score);
    return `<strong class="rating-label">Rating:</strong> ${renderStarRatingHtml(val)}`;
  });

  // X/5 stars or X.X/5 stars
  result = result.replace(/([0-5](?:\.[0-9])?)\s*\/\s*5\s*stars?/gi, (match, score) => {
    const val = parseFloat(score);
    return renderStarRatingHtml(val);
  });

  return result;
};

const ContentDisplayOne = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [region, setRegion] = useState('');
  const [title, setTitle] = useState('');
  const [placeDetails, setPlaceDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const carouselRef = useRef(null);
  const [originalTitle, setOriginalTitle] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupImages, setPopupImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    let newRegion = '';
    let newTitle = '';
    if (params.region && params.title) {
      newRegion = decodeURIComponent(params.region);
      newTitle = decodeURIComponent(params.title);
    } else if (location.state && location.state.region && location.state.title) {
      newRegion = location.state.region;
      newTitle = location.state.title;
    } else if (location.pathname) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2) {
        newRegion = decodeURIComponent(pathParts[pathParts.length - 2]);
        newTitle = decodeURIComponent(pathParts[pathParts.length - 1]);
      }
    }
    setRegion(newRegion);
    setTitle(newTitle);
    if (!originalTitle) {
      setOriginalTitle(newTitle);
    }
  }, [params.region, params.title, location.state, location.pathname]);

  useEffect(() => {
    const fetchPlaceDetails = async () => {
      if (!region || !title) {
        setError('Region or title is missing');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const encodedRegion = encodeURIComponent(region);
        const encodedTitle = encodeURIComponent(title);
        const url = `/api/placedetails/${encodedRegion}/${encodedTitle}`;
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPlaceDetails(data);
        setError(null);
      } catch (e) {
        console.error('Fetch error:', e);
        setError(`Failed to fetch details for ${title} in ${region}. Error: ${e.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    if (region && title) {
      fetchPlaceDetails();
    }
  }, [region, title]);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleImageClick = (imageTitle) => {
    const encodedRegion = encodeURIComponent(placeDetails.region);
    const encodedTitle = encodeURIComponent(imageTitle);
    navigate(`/content-display-one/${encodedRegion}/${encodedTitle}`, { state: { originalTitle: originalTitle || title } });
  };

  const handleBackClick = () => {
    if (originalTitle && originalTitle !== title) {
      const encodedRegion = encodeURIComponent(region);
      const encodedOriginalTitle = encodeURIComponent(originalTitle);
      navigate(`/content-display-one/${encodedRegion}/${encodedOriginalTitle}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const openPopup = (images, startIndex) => {
    setPopupImages(images);
    setCurrentImageIndex(startIndex);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % popupImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + popupImages.length) % popupImages.length);
  };

  const handleDayImageClick = (day, imgIndex) => {
    const images = Object.values(day.images);
    openPopup(images, imgIndex);
  };

  return (
    <Layout>
      {placeDetails && (
        <Helmet>
          <title>{`${placeDetails.title} - ${region} Travelogue | The Half Time Travel`}</title>
          <meta
            name="description"
            content={placeDetails.description ? placeDetails.description.replace(/<[^>]*>?/gm, '').substring(0, 160) : `Detailed travel guide for ${placeDetails.title} in ${region}.`}
          />
          <meta property="og:title" content={`${placeDetails.title} Travelogue | The Half Time Travel`} />
          <meta property="og:description" content={placeDetails.title} />
          {placeDetails.image && <meta property="og:image" content={getCdnImageUrl(placeDetails.image)} />}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "TouristAttraction",
              "name": placeDetails.title,
              "description": placeDetails.description ? placeDetails.description.replace(/<[^>]*>?/gm, '') : '',
              "image": placeDetails.image ? getCdnImageUrl(placeDetails.image) : '',
              "isPartOf": {
                "@type": "TravelBlog",
                "name": "The Half Time Travel Website"
              }
            })}
          </script>
        </Helmet>
      )}

      <div style={{ padding: '20px', minHeight: '60vh' }}>
        {isLoading && <div style={{ textAlign: 'center', padding: '40px' }}>Loading travel details...</div>}
        {error && <div style={{ color: 'red', padding: '20px', textAlign: 'center' }}>{error}</div>}

        {!isLoading && !error && placeDetails && (
          <div className="content-display">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h1 style={{ margin: 0 }}>{region} Region</h1>
              <button onClick={handleBackClick} className="back-button">
                Back to {originalTitle || "Previous"} Page
              </button>
            </div>

            <div className="place-details">
              <div className="image-container">
                {placeDetails.image && (
                  <img
                    src={getCdnImageUrl(placeDetails.image)}
                    alt={placeDetails.title}
                    className="main-image"
                  />
                )}
                <h1 className="image-title">{placeDetails.title}</h1>
              </div>

              {placeDetails.imageBlock && placeDetails.imageBlock.length > 0 && (
                <div className="image-block-carousel">
                  <button onClick={() => scrollCarousel('left')} className="carousel-button left">&lt;</button>
                  <div className="carousel" ref={carouselRef}>
                    {placeDetails.imageBlock.map((item, index) => (
                      <div key={index} className="carousel-item">
                        <img
                          src={getCdnImageUrl(item.image)}
                          alt={item.title}
                          className="carousel-image clickable"
                          onClick={() => handleImageClick(item.title)}
                        />
                        <h3 className="carousel-title">{item.title}</h3>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => scrollCarousel('right')} className="carousel-button right">&gt;</button>
                </div>
              )}

              {placeDetails.rating > 0 && (
                <div className="recommendation-box-container">
                  <h3>Recommendation</h3>
                  <div className="recommendation-stars-row">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`recommendation-star ${star <= placeDetails.rating ? 'filled' : 'empty'}`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {placeDetails.description && (
                <div
                  className="main-description"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processHtmlWithRatings(placeDetails.description)) }}
                />
              )}
            </div>

            {placeDetails.days && placeDetails.days.map((day, index) => (
              <div key={day._id || index} className="day-block">
                <h2>{day.title}</h2>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processHtmlWithRatings(day.description)) }} />

                {day.date && (
                  <div className="day-date-visit">
                    <span className="calendar-icon">📅</span> <strong>Date of Visit:</strong> {day.date}
                  </div>
                )}

                {day.images && Object.keys(day.images).length > 0 && (
                  <div className="image-gallery">
                    {Object.values(day.images).map((image, imgIndex) => (
                      <img
                        key={imgIndex}
                        src={getCdnImageUrl(image)}
                        alt={`Day ${index + 1} - Image ${imgIndex + 1}`}
                        onClick={() => handleDayImageClick(day, imgIndex)}
                        className="clickable"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

            {placeDetails.commonBlocks && placeDetails.commonBlocks.map((block, index) => (
              <div key={block._id || index} className="common-block">
                <h3>{block.title}</h3>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processHtmlWithRatings(block.description)) }} />

                {block.date && (
                  <div className="day-date-visit">
                    <span className="calendar-icon">📅</span> <strong>Date:</strong> {block.date}
                  </div>
                )}

                {block.images && Object.keys(block.images).length > 0 && (
                  <div className="image-gallery">
                    {Object.values(block.images).map((image, imgIndex) => (
                      <img
                        key={imgIndex}
                        src={getCdnImageUrl(image)}
                        alt={`${block.title || 'Block'} - Image ${imgIndex + 1}`}
                        onClick={() => openPopup(Object.values(block.images), imgIndex)}
                        className="clickable"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={getCdnImageUrl(popupImages[currentImageIndex])}
              alt={`Popup image ${currentImageIndex + 1}`}
              className="popup-image"
            />
            <button className="popup-close" onClick={closePopup}>&times;</button>
            <button className="popup-nav prev" onClick={prevImage}>&lt;</button>
            <button className="popup-nav next" onClick={nextImage}>&gt;</button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContentDisplayOne;
